import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const V_MID = "#EDE9FE"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"
const SUCCESS = "#059669"
const SUCCESS_BG = "#ECFDF5"
const SUCCESS_MID = "#D1FAE5"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"

const METRICS_DEF = {
  es: [
    { id:"seguidores",  label:"Seguidores",       icon:"👥", unit:"",    color:"#7C3AED", desc:"Total de seguidores en tu red principal" },
    { id:"alcance",     label:"Alcance semanal",  icon:"📡", unit:"",    color:"#2563EB", desc:"Personas únicas que vieron tu contenido" },
    { id:"interaccion", label:"Interacciones",    icon:"❤️", unit:"",    color:"#DC2626", desc:"Likes, comentarios, compartidos y guardados" },
    { id:"clics",       label:"Clics al perfil",  icon:"🔗", unit:"",    color:"#EA580C", desc:"Personas que fueron a tu perfil o bio" },
    { id:"ventas",      label:"Ventas / Leads",   icon:"💰", unit:"$",   color:"#059669", desc:"Ingresos generados o leads calificados" },
    { id:"tasa",        label:"Tasa de engagement",icon:"📊",unit:"%",   color:"#0891B2", desc:"Interacciones ÷ Alcance × 100" },
  ],
  en: [
    { id:"seguidores",  label:"Followers",        icon:"👥", unit:"",    color:"#7C3AED", desc:"Total followers on your main network" },
    { id:"alcance",     label:"Weekly reach",     icon:"📡", unit:"",    color:"#2563EB", desc:"Unique people who saw your content" },
    { id:"interaccion", label:"Interactions",     icon:"❤️", unit:"",    color:"#DC2626", desc:"Likes, comments, shares and saves" },
    { id:"clics",       label:"Profile clicks",   icon:"🔗", unit:"",    color:"#EA580C", desc:"People who went to your profile or bio" },
    { id:"ventas",      label:"Sales / Leads",    icon:"💰", unit:"$",   color:"#059669", desc:"Revenue generated or qualified leads" },
    { id:"tasa",        label:"Engagement rate",  icon:"📊", unit:"%",   color:"#0891B2", desc:"Interactions ÷ Reach × 100" },
  ]
}

// Simulated historical data — in production comes from Supabase
const HISTORY = [
  { week:"Sem 1", label:"7 Abr", seguidores:1240, alcance:3800, interaccion:420, clics:85,  ventas:350,  tasa:11.1 },
  { week:"Sem 2", label:"14 Abr", seguidores:1310, alcance:4200, interaccion:510, clics:102, ventas:580,  tasa:12.1 },
  { week:"Sem 3", label:"21 Abr", seguidores:1380, alcance:3900, interaccion:480, clics:91,  ventas:420,  tasa:12.3 },
  { week:"Sem 4", label:"28 Abr", seguidores:1450, alcance:5100, interaccion:640, clics:128, ventas:890,  tasa:12.5 },
  { week:"Sem 5", label:"5 May",  seguidores:1520, alcance:5600, interaccion:710, clics:144, ventas:1050, tasa:12.7 },
]

const HISTORY_EN = [
  { week:"Wk 1", label:"Apr 7",  seguidores:1240, alcance:3800, interaccion:420, clics:85,  ventas:350,  tasa:11.1 },
  { week:"Wk 2", label:"Apr 14", seguidores:1310, alcance:4200, interaccion:510, clics:102, ventas:580,  tasa:12.1 },
  { week:"Wk 3", label:"Apr 21", seguidores:1380, alcance:3900, interaccion:480, clics:91,  ventas:420,  tasa:12.3 },
  { week:"Wk 4", label:"Apr 28", seguidores:1450, alcance:5100, interaccion:640, clics:128, ventas:890,  tasa:12.5 },
  { week:"Wk 5", label:"May 5",  seguidores:1520, alcance:5600, interaccion:710, clics:144, ventas:1050, tasa:12.7 },
]

function MiniChart({ data, metricId, color, height=80 }) {
  const values = data.map(d => d[metricId] || 0)
  const max = Math.max(...values) || 1
  const min = Math.min(...values)
  const range = max - min || 1
  const w = 100 / (values.length - 1)
  const pts = values.map((v, i) => {
    const x = i * w
    const y = 100 - ((v - min) / range * 80 + 10)
    return `${x},${y}`
  }).join(" ")
  const areaBottom = `${(values.length-1)*w},100 0,100`
  const area = `${pts} ${areaBottom}`

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width:"100%", height, display:"block" }}>
      <defs>
        <linearGradient id={`g-${metricId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.01"/>
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#g-${metricId})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      {values.map((v, i) => (
        <circle key={i} cx={i*w} cy={100-((v-min)/range*80+10)} r="2.5" fill={color}/>
      ))}
    </svg>
  )
}

function BigChart({ data, metricId, color, labels, height=180 }) {
  const values = data.map(d => d[metricId] || 0)
  const max = Math.max(...values) || 1
  const min = Math.min(...values)
  const range = max - min || 1
  const n = values.length
  const w = 100 / (n - 1)
  const pts = values.map((v, i) => {
    const x = i * w
    const y = 100 - ((v - min) / range * 75 + 12)
    return `${x},${y}`
  }).join(" ")
  const area = `${pts} ${(n-1)*w},100 0,100`

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width:"100%", height, display:"block" }}>
      <defs>
        <linearGradient id={`bg-${metricId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {[25,50,75].map(y => (
        <line key={y} x1="0" y1={y} x2="100" y2={y} stroke={BORDER} strokeWidth="0.5" strokeDasharray="2,2"/>
      ))}
      <polygon points={area} fill={`url(#bg-${metricId})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {values.map((v, i) => (
        <circle key={i} cx={i*w} cy={100-((v-min)/range*75+12)} r="2" fill={WHITE} stroke={color} strokeWidth="1.5"/>
      ))}
    </svg>
  )
}

function delta(arr, key) {
  if (arr.length < 2) return 0
  const last = arr[arr.length-1][key] || 0
  const prev = arr[arr.length-2][key] || 0
  if (!prev) return 0
  return ((last - prev) / prev * 100).toFixed(1)
}

export default function KorivaTracker() {
  const [lang, setLang]         = useState("es")
  const navigate = useNavigate()
  const [view, setView]         = useState("dashboard")
  const [detailMetric, setDetailMetric] = useState(null)
  const [inputs, setInputs]     = useState({})
  const [saved, setSaved]       = useState(false)
  const [history, setHistory]   = useState(HISTORY)
  const [activeNetworks, setActiveNetworks] = useState(["Instagram"])
  const [nombreProyecto, setNombreProyecto] = useState("")

  const es      = lang === "es"
  const metrics = METRICS_DEF[lang]

  // Leer el historial real de Supabase al abrir
  useEffect(() => {
    async function cargarHistorial() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        // Leer el idioma guardado del usuario
        const { data: userData } = await supabase
          .from("users").select("lang").eq("id", session.user.id).maybeSingle()
        if (userData && userData.lang) setLang(userData.lang)
        const { data: proyectos } = await supabase
          .from("projects").select("id, nombre").eq("user_id", session.user.id).limit(1)
        if (!proyectos || proyectos.length === 0) return
        setNombreProyecto(proyectos[0].nombre || "")
        const { data: registros } = await supabase
          .from("tracker").select("*").eq("project_id", proyectos[0].id).order("created_at", { ascending: true })
        if (registros && registros.length > 0) {
          const traducidos = registros.map((r, i) => ({
            week: es ? `Sem ${i+1}` : `Wk ${i+1}`,
            label: r.semana ? new Date(r.semana).toLocaleDateString(es?"es-MX":"en-US",{day:"numeric",month:"short"}) : "",
            seguidores: r.seguidores || 0,
            alcance: r.alcance || 0,
            interaccion: r.interacciones || 0,
            clics: r.clics_perfil || 0,
            ventas: r.ventas_leads || 0,
            tasa: r.engagement || 0,
          }))
          setHistory(traducidos)
        } else {
          setHistory([])
        }
      } catch (e) {
        console.error("Error cargando historial del tracker:", e)
      }
    }
    cargarHistorial()
  }, [es])

  const hist    = history
  const latest  = hist[hist.length-1] || {}

  // Plan simulation — in production comes from Supabase user data
  const USER_PLAN = "starter"
  const NETWORK_LIMITS = { starter:1, growth:2, agency:4 }
  const maxNetworks = NETWORK_LIMITS[USER_PLAN]
  const ALL_NETWORKS = ["Instagram","TikTok","Facebook","LinkedIn"]

  function toggleNetwork(n) {
    if (activeNetworks.includes(n)) {
      if (activeNetworks.length === 1) return // always keep at least one
      setActiveNetworks(prev => prev.filter(x => x !== n))
    } else {
      if (activeNetworks.length >= maxNetworks) return // plan limit
      setActiveNetworks(prev => [...prev, n])
    }
  }

  const network = activeNetworks[0] || "Instagram"

  async function saveWeek() {
    const newEntry = {
      week: es ? `Sem ${hist.length+1}` : `Wk ${hist.length+1}`,
      label: new Date().toLocaleDateString(es?"es-MX":"en-US",{day:"numeric",month:"short"}),
      seguidores:  parseFloat(inputs.seguidores||latest.seguidores||0),
      alcance:     parseFloat(inputs.alcance||0),
      interaccion: parseFloat(inputs.interaccion||0),
      clics:       parseFloat(inputs.clics||0),
      ventas:      parseFloat(inputs.ventas||0),
      tasa:        parseFloat(inputs.tasa||0),
    }
    setHistory(h => [...h, newEntry])
    setInputs({})
    setSaved(true)
    // Guardar en Supabase (traduciendo nombres a las columnas de la tabla tracker)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        const { data: proyectos } = await supabase
          .from("projects").select("id").eq("user_id", session.user.id).limit(1)
        if (proyectos && proyectos.length > 0) {
          const { error } = await supabase.from("tracker").insert({
            project_id: proyectos[0].id,
            red: network,
            semana: new Date().toISOString().slice(0,10),
            seguidores: Math.round(newEntry.seguidores) || 0,
            alcance: Math.round(newEntry.alcance) || 0,
            interacciones: Math.round(newEntry.interaccion) || 0,
            clics_perfil: Math.round(newEntry.clics) || 0,
            ventas_leads: newEntry.ventas || 0,
            engagement: newEntry.tasa || 0,
          })
          if (error) console.error("Error guardando métricas en tracker:", error)
        }
      }
    } catch (e) {
      console.error("Error guardando métricas:", e)
    }
    setTimeout(()=>{setSaved(false);setView("dashboard")},1800)
  }

  const T = {
    es:{
      title:"Tracker de crecimiento", sub:"Registra tus métricas cada semana. KORIVA las grafica y muestra tu evolución.",
      network:"Red social principal", addWeek:"Registrar esta semana",
      dashboard:"Dashboard", inputView:"Registrar semana", detail:"Detalle",
      thisWeek:"Esta semana", vsLast:"vs semana anterior",
      noData:"Sin dato", saving:"Guardando...", saved:"✓ Guardado",
      inputTitle:"Registra tu semana", inputSub:"Ingresa las métricas de esta semana. KORIVA las graficará automáticamente.",
      saveBtn:"Guardar y graficar →",
      allMetrics:"Todas las métricas", evolution:"Evolución",
      weeks:"semanas registradas", insight:"Tendencia",
      up:"↑ en crecimiento", down:"↓ en caída", stable:"→ estable",
      growth:"crecimiento", vsW:"vs semana anterior",
      tip:"💡 ",
      tips:{
        seguidores:"Tu crecimiento de seguidores es constante. Sigue publicando con consistencia para mantener el momentum.",
        alcance:"El alcance subió esta semana. Los Reels y Carruseles están funcionando bien.",
        interaccion:"Las interacciones están creciendo. Tu contenido está conectando con tu audiencia.",
        clics:"Los clics al perfil subieron. Asegúrate de que tu bio y enlace estén optimizados.",
        ventas:"Las ventas están en aumento. Este es el mejor momento para hacer una oferta especial.",
        tasa:"Tu tasa de engagement está por encima del promedio de la industria (5-10%).",
      },
      backBtn:"← Volver",
    },
    en:{
      title:"Growth tracker", sub:"Record your metrics every week. KORIVA graphs them and shows your evolution.",
      network:"Main social network", addWeek:"Record this week",
      dashboard:"Dashboard", inputView:"Record week", detail:"Detail",
      thisWeek:"This week", vsLast:"vs last week",
      noData:"No data", saving:"Saving...", saved:"✓ Saved",
      inputTitle:"Record your week", inputSub:"Enter this week's metrics. KORIVA will automatically graph them.",
      saveBtn:"Save and graph →",
      allMetrics:"All metrics", evolution:"Evolution",
      weeks:"weeks recorded", insight:"Trend",
      up:"↑ growing", down:"↓ declining", stable:"→ stable",
      growth:"growth", vsW:"vs last week",
      tip:"💡 ",
      tips:{
        seguidores:"Your follower growth is consistent. Keep posting regularly to maintain momentum.",
        alcance:"Reach went up this week. Reels and Carousels are working well.",
        interaccion:"Interactions are growing. Your content is connecting with your audience.",
        clics:"Profile clicks went up. Make sure your bio and link are optimized.",
        ventas:"Sales are increasing. This is the best time to run a special offer.",
        tasa:"Your engagement rate is above the industry average (5-10%).",
      },
      backBtn:"← Back",
    }
  }
  const t = T[lang]

  const s = {
    page:      { minHeight:"100vh", background:WHITE, fontFamily:B, color:TEXT },
    header:    { borderBottom:`1px solid ${BORDER}`, padding:".85rem 2rem", display:"flex", alignItems:"center", justifyContent:"space-between" },
    logo:      { fontFamily:H, fontSize:17, fontWeight:800, color:TEXT, letterSpacing:"-.02em" },
    logoV:     { color:V },
    hRight:    { display:"flex", alignItems:"center", gap:"1rem" },
    brandChip: { display:"flex", alignItems:"center", gap:6, background:OFF, border:`1px solid ${BORDER}`, borderRadius:20, padding:"4px 12px" },
    brandDot:  { width:6, height:6, background:SUCCESS, borderRadius:"50%" },
    brandName: { fontSize:12, fontWeight:600, color:TEXT },
    langRow:   { display:"flex", gap:3, background:OFF, borderRadius:8, padding:3, border:`1px solid ${BORDER}` },
    langBtn:   (a) => ({ background:a?V:"transparent", color:a?WHITE:MUTED, border:"none", borderRadius:6, padding:"4px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:B, transition:"all .18s" }),
    nav:       { borderBottom:`1px solid ${BORDER}`, padding:"0 2rem", display:"flex", gap:0 },
    navTab:    (a) => ({ padding:".8rem 1.1rem", border:"none", borderBottom:a?`2px solid ${V}`:"2px solid transparent", background:"transparent", fontFamily:B, fontSize:13, fontWeight:a?700:400, color:a?V:MUTED, cursor:"pointer", transition:"all .2s", marginBottom:-1 }),
    wrap:      { maxWidth:900, margin:"0 auto", padding:"2rem 1.5rem" },
    topRow:    { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1.5rem" },
    topLeft:   {},
    topLbl:    { fontSize:11, fontWeight:700, letterSpacing:".14em", color:V, textTransform:"uppercase", marginBottom:".3rem", fontFamily:B },
    pageTitle: { fontFamily:H, fontSize:24, fontWeight:800, color:TEXT, letterSpacing:"-.03em" },
    addBtn:    { padding:"10px 18px", background:V, color:WHITE, border:"none", borderRadius:8, fontFamily:H, fontSize:13, fontWeight:700, cursor:"pointer", letterSpacing:"-.01em" },
    networkRow:{ display:"flex", gap:6, marginBottom:"1.5rem", flexWrap:"wrap" },
    netBtn:    (a) => ({ padding:"5px 14px", borderRadius:20, border:a?`2px solid ${V}`:`1.5px solid ${BORDER}`, background:a?V_SOFT:WHITE, color:a?V:MUTED, fontSize:12, fontWeight:a?700:400, cursor:"pointer", fontFamily:B, transition:"all .18s" }),
    kpiGrid:   { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:"1.5rem" },
    kpiCard:   (color) => ({ background:WHITE, border:`1.5px solid ${BORDER}`, borderRadius:12, padding:"1rem 1.1rem", cursor:"pointer", transition:"all .2s", position:"relative", overflow:"hidden" }),
    kpiTop:    { display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:".3rem" },
    kpiLabel:  { fontSize:11, fontWeight:600, color:MUTED, fontFamily:B },
    kpiIcon:   { fontSize:16 },
    kpiValue:  (color) => ({ fontFamily:H, fontSize:26, fontWeight:800, color:TEXT, letterSpacing:"-.03em", marginBottom:".2rem" }),
    kpiDelta:  (pos) => ({ fontSize:11, fontWeight:600, color:pos>0?SUCCESS:pos<0?"#DC2626":MUTED, display:"flex", alignItems:"center", gap:3 }),
    kpiChart:  { marginTop:".5rem", marginLeft:-8, marginRight:-8 },
    insightBox:{ background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:12, padding:".9rem 1.1rem", marginBottom:"1.5rem" },
    insightLbl:{ fontSize:10, fontWeight:700, letterSpacing:".12em", color:V, textTransform:"uppercase", marginBottom:".5rem", fontFamily:B },
    insightGrid:{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:".6rem" },
    insightItem:{ fontSize:12, color:TEXT, lineHeight:1.55 },
    bigChartWrap:{ background:WHITE, border:`1px solid ${BORDER}`, borderRadius:14, padding:"1.25rem 1.4rem", marginBottom:"1.5rem" },
    bigChartTop:{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" },
    bigChartTitle:{ fontFamily:H, fontSize:16, fontWeight:700, color:TEXT, letterSpacing:"-.01em" },
    bigChartSub:{ fontSize:12, color:MUTED },
    bigChartLabels:{ display:"flex", justifyContent:"space-between", marginTop:".5rem" },
    bigChartLbl:{ fontSize:10, color:MUTED, fontFamily:B },
    inputWrap:  { maxWidth:560, margin:"0 auto" },
    inputTitle: { fontFamily:H, fontSize:26, fontWeight:800, color:TEXT, letterSpacing:"-.03em", marginBottom:".4rem" },
    inputSub:   { fontSize:14, color:MUTED, lineHeight:1.65, marginBottom:"1.5rem" },
    inputGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:"1.5rem" },
    inputCard:  { background:OFF, border:`1px solid ${BORDER}`, borderRadius:12, padding:"1rem" },
    inputCardTop:{ display:"flex", alignItems:"center", gap:6, marginBottom:.4 },
    inputIco:   { fontSize:18 },
    inputLbl:   { fontSize:12, fontWeight:700, color:TEXT, fontFamily:H },
    inputDesc:  { fontSize:11, color:MUTED, marginBottom:".6rem" },
    inputField: { width:"100%", border:`1.5px solid ${BORDER}`, background:WHITE, borderRadius:7, padding:"9px 12px", fontFamily:B, fontSize:14, color:TEXT, outline:"none", boxSizing:"border-box", transition:"border .2s" },
    prevRow:    { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:".35rem" },
    prevLbl:    { fontSize:11, color:MUTED },
    prevVal:    { fontSize:11, fontWeight:600, color:TEXT },
    saveBtn:    { width:"100%", padding:"13px", background:V, color:WHITE, border:"none", borderRadius:9, fontFamily:H, fontSize:14, fontWeight:700, cursor:"pointer", letterSpacing:"-.01em", transition:"all .2s" },
    savedMsg:   { width:"100%", padding:"13px", background:SUCCESS_BG, color:SUCCESS, border:`1px solid ${SUCCESS_MID}`, borderRadius:9, fontFamily:H, fontSize:14, fontWeight:700, textAlign:"center" },
    detailWrap: { maxWidth:680, margin:"0 auto" },
    detailBack: { background:"none", border:"none", color:MUTED, fontSize:13, cursor:"pointer", fontFamily:B, display:"flex", alignItems:"center", gap:4, marginBottom:"1.25rem" },
    detailTitle:{ fontFamily:H, fontSize:24, fontWeight:800, color:TEXT, letterSpacing:"-.03em", marginBottom:".3rem" },
    detailVal:  (color) => ({ fontFamily:H, fontSize:40, fontWeight:800, color, letterSpacing:"-.04em", marginBottom:".2rem" }),
    detailDelta:(pos) => ({ fontSize:14, fontWeight:600, color:pos>0?SUCCESS:pos<0?"#DC2626":MUTED, marginBottom:"1.5rem" }),
    histTable:  { background:OFF, border:`1px solid ${BORDER}`, borderRadius:12, overflow:"hidden" },
    histHead:   { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", padding:".6rem 1rem", background:WHITE, borderBottom:`1px solid ${BORDER}` },
    histHeadLbl:{ fontSize:10, fontWeight:700, color:MUTED, textTransform:"uppercase", letterSpacing:".08em", fontFamily:B },
    histRow:    (i) => ({ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", padding:".7rem 1rem", background:i%2===0?WHITE:OFF, borderBottom:`1px solid ${BORDER}` }),
    histCell:   { fontSize:13, color:TEXT, fontWeight:400 },
    histCellBold:{ fontSize:13, color:TEXT, fontWeight:700, fontFamily:H },
    tipBox:     { background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:10, padding:".85rem 1rem", marginTop:"1.25rem", fontSize:13, color:"#92400E", lineHeight:1.65 },
  }

  const currentDetailMetric = metrics.find(m => m.id === detailMetric)
  const d = delta(hist, detailMetric||"seguidores")

  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap');
        @keyframes fi{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .fi{animation:fi .3s ease}
        input:focus{border-color:${V}!important}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-thumb{background:${BORDER};border-radius:2px}
      `}</style>

      <div style={s.header}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
          <button onClick={()=>navigate("/dashboard")} style={{background:"none",border:"none",color:V,fontFamily:B,fontSize:13,fontWeight:700,cursor:"pointer",padding:"4px 0",display:"flex",alignItems:"center",gap:5}}>
            <span>←</span><span>{es ? "Mis proyectos" : "My projects"}</span>
          </button>
        </div>
        <div style={s.hRight}>
          <div style={s.brandChip}><div style={s.brandDot}/><span style={s.brandName}>{nombreProyecto || (es?"Mi negocio":"My business")}</span></div>
          <div style={s.langRow}>
            <button style={s.langBtn(es)} onClick={()=>setLang("es")}>ES</button>
            <button style={s.langBtn(!es)} onClick={()=>setLang("en")}>EN</button>
          </div>
        </div>
      </div>

      <div style={s.nav}>
        <button style={s.navTab(view==="dashboard")} onClick={()=>setView("dashboard")}>{t.dashboard}</button>
        <button style={s.navTab(view==="input")} onClick={()=>setView("input")}>{t.inputView}</button>
      </div>

      <div style={s.wrap}>

        {/* ── DASHBOARD ── */}
        {view==="dashboard" && (
          <div className="fi">
            <div style={s.topRow}>
              <div style={s.topLeft}>
                <div style={s.topLbl}>{t.title}</div>
                <div style={s.pageTitle}>{hist.length} {t.weeks}</div>
              </div>
              <button style={s.addBtn} onClick={()=>setView("input")}>+ {t.addWeek}</button>
            </div>

            {/* NETWORK SELECTOR */}
            <div style={{ marginBottom:"1.25rem" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ fontSize:12, color:MUTED, fontFamily:B }}>
                  {es?"Redes activas":"Active networks"} · {activeNetworks.length}/{maxNetworks} {es?"disponibles en tu plan":"available in your plan"}
                </div>
                {USER_PLAN==="starter" && (
                  <div style={{ fontSize:10, color:V, fontWeight:600, fontFamily:B, background:V_SOFT, border:`1px solid ${V_MID}`, borderRadius:10, padding:"2px 8px", cursor:"pointer" }}>
                    {es?"Más redes en Growth →":"More networks in Growth →"}
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {ALL_NETWORKS.map(n => {
                  const isActive = activeNetworks.includes(n)
                  const isLocked = !isActive && activeNetworks.length >= maxNetworks
                  return (
                    <button key={n}
                      onClick={() => toggleNetwork(n)}
                      style={{
                        padding:"5px 14px", borderRadius:20, fontFamily:B, fontSize:12, fontWeight:isActive?700:400,
                        cursor: isLocked ? "not-allowed" : "pointer",
                        border: isActive ? `2px solid ${V}` : `1.5px solid ${BORDER}`,
                        background: isActive ? V_SOFT : isLocked ? OFF : WHITE,
                        color: isActive ? V : isLocked ? BORDER : MUTED,
                        opacity: isLocked ? 0.5 : 1,
                        transition:"all .18s",
                        display:"flex", alignItems:"center", gap:4
                      }}>
                      {isLocked && <span style={{fontSize:10}}>🔒</span>}
                      {n}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* KPI CARDS */}
            <div style={s.kpiGrid}>
              {metrics.map(m=>{
                const val  = latest[m.id] || 0
                const dlt  = parseFloat(delta(hist, m.id))
                const pos  = dlt > 0 ? 1 : dlt < 0 ? -1 : 0
                return (
                  <div key={m.id} style={s.kpiCard(m.color)} onClick={()=>{setDetailMetric(m.id);setView("detail")}}>
                    <div style={s.kpiTop}>
                      <div style={s.kpiLabel}>{m.label}</div>
                      <span style={s.kpiIcon}>{m.icon}</span>
                    </div>
                    <div style={s.kpiValue(m.color)}>
                      {m.unit}{val.toLocaleString()}
                      {m.unit==="%"?"%":""}
                    </div>
                    <div style={s.kpiDelta(pos)}>
                      {pos>0?"↑":pos<0?"↓":"→"} {Math.abs(dlt)}% {t.vsW}
                    </div>
                    <div style={s.kpiChart}>
                      <MiniChart data={hist} metricId={m.id} color={m.color} height={52}/>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* INSIGHTS */}
            <div style={s.insightBox}>
              <div style={s.insightLbl}>📊 {t.insight}</div>
              <div style={s.insightGrid}>
                {metrics.slice(0,3).map(m=>{
                  const dlt = parseFloat(delta(hist,m.id))
                  const trend = dlt>3?t.up:dlt<-3?t.down:t.stable
                  return (
                    <div key={m.id} style={s.insightItem}>
                      <strong>{m.label}</strong><br/>
                      <span style={{color:dlt>0?SUCCESS:dlt<0?"#DC2626":MUTED}}>{trend}</span>
                      {dlt!==0&&<span style={{color:MUTED}}> · {Math.abs(dlt)}%</span>}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* BIG CHART — followers */}
            <div style={s.bigChartWrap}>
              <div style={s.bigChartTop}>
                <div>
                  <div style={s.bigChartTitle}>{metrics[0].label}</div>
                  <div style={s.bigChartSub}>{activeNetworks.join(" · ")} · {hist.length} {t.weeks}</div>
                </div>
                <div style={{ fontFamily:H, fontSize:22, fontWeight:800, color:V }}>
                  {latest.seguidores?.toLocaleString()}
                </div>
              </div>
              <BigChart data={hist} metricId="seguidores" color={V} height={160}/>
              <div style={s.bigChartLabels}>
                {hist.map((h,i)=>(
                  <span key={i} style={s.bigChartLbl}>{h.label}</span>
                ))}
              </div>
            </div>

            {/* BIG CHART — sales */}
            <div style={s.bigChartWrap}>
              <div style={s.bigChartTop}>
                <div>
                  <div style={s.bigChartTitle}>{metrics[4].label}</div>
                  <div style={s.bigChartSub}>{activeNetworks.join(" · ")} · {hist.length} {t.weeks}</div>
                </div>
                <div style={{ fontFamily:H, fontSize:22, fontWeight:800, color:SUCCESS }}>
                  ${latest.ventas?.toLocaleString()}
                </div>
              </div>
              <BigChart data={hist} metricId="ventas" color={SUCCESS} height={160}/>
              <div style={s.bigChartLabels}>
                {hist.map((h,i)=>(
                  <span key={i} style={s.bigChartLbl}>{h.label}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── INPUT ── */}
        {view==="input" && (
          <div className="fi">
            <div style={s.inputWrap}>
              <div style={s.topLbl}>{t.inputView}</div>
              <div style={s.inputTitle}>{t.inputTitle}</div>
              <p style={s.inputSub}>{t.inputSub}</p>

              <div style={s.inputGrid}>
                {metrics.map(m=>(
                  <div key={m.id} style={s.inputCard}>
                    <div style={s.inputCardTop}>
                      <span style={s.inputIco}>{m.icon}</span>
                      <div style={s.inputLbl}>{m.label}</div>
                    </div>
                    <div style={s.inputDesc}>{m.desc}</div>
                    <div style={s.prevRow}>
                      <span style={s.prevLbl}>{es?"Semana anterior":"Last week"}</span>
                      <span style={s.prevVal}>{m.unit}{(latest[m.id]||0).toLocaleString()}{m.unit==="%"?"%":""}</span>
                    </div>
                    <input
                      style={s.inputField}
                      type="number"
                      value={inputs[m.id]||""}
                      onChange={e=>setInputs(p=>({...p,[m.id]:e.target.value}))}
                      placeholder={`${m.unit}${latest[m.id]||0}`}
                    />
                  </div>
                ))}
              </div>

              {saved
                ? <div style={s.savedMsg}>{t.saved}</div>
                : <button style={s.saveBtn} onClick={saveWeek}>{t.saveBtn}</button>
              }
            </div>
          </div>
        )}

        {/* ── DETAIL ── */}
        {view==="detail" && currentDetailMetric && (
          <div className="fi">
            <div style={s.detailWrap}>
              <button style={s.detailBack} onClick={()=>setView("dashboard")}>← {t.backBtn}</button>
              <div style={s.topLbl}>{t.evolution}</div>
              <div style={s.detailTitle}>{currentDetailMetric.label}</div>
              <div style={s.detailVal(currentDetailMetric.color)}>
                {currentDetailMetric.unit}{(latest[currentDetailMetric.id]||0).toLocaleString()}{currentDetailMetric.unit==="%"?"%":""}
              </div>
              <div style={s.detailDelta(parseFloat(d))}>
                {parseFloat(d)>0?"↑":parseFloat(d)<0?"↓":"→"} {Math.abs(d)}% {t.vsW}
              </div>

              <BigChart data={hist} metricId={currentDetailMetric.id} color={currentDetailMetric.color} height={200}/>
              <div style={s.bigChartLabels}>
                {hist.map((h,i)=><span key={i} style={s.bigChartLbl}>{h.label}</span>)}
              </div>
              <div style={{...s.histTable,marginTop:"1.25rem"}}>
                <div style={s.histHead}>
                  <div style={s.histHeadLbl}>{es?"Semana":"Week"}</div>
                  <div style={s.histHeadLbl}>{es?"Fecha":"Date"}</div>
                  <div style={s.histHeadLbl}>{currentDetailMetric.label}</div>
                </div>
                {[...hist].reverse().map((h,i)=>(
                  <div key={i} style={s.histRow(i)}>
                    <div style={s.histCellBold}>{h.week}</div>
                    <div style={s.histCell}>{h.label}</div>
                    <div style={s.histCellBold}>{currentDetailMetric.unit}{(h[currentDetailMetric.id]||0).toLocaleString()}{currentDetailMetric.unit==="%"?"%":""}</div>
                  </div>
                ))}
              </div>

              <div style={s.tipBox}>{t.tip}{t.tips[currentDetailMetric.id]}</div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
